import { useMemo } from "react";
import { useBulkRecentMattersQuery, useGetCurrentUserQuery } from "utils/wemble-api";
import { PublicUserDto, UserMattersEntry } from "utils/wemble-api.generated";

// Adapter types — maps UserMattersEntry to the shape DetailedTable expects
interface DetailedMatter {
  displayName?: string;
  clientDisplayName?: string;
  clientCountry?: string;
  confidential?: boolean;
  number?: string;
  nonBillable?: boolean;
  totalHours?: number;
  resposibleTimekeeperName?: string;
  comment?: string;
  manager?: string;
}
interface UserDetailed {
  displayName?: string;
  profilePicture?: string;
  title?: string;
  userId?: string;
  currentWorkload?: number | null;
  onLeave?: boolean | null;
  matters?: DetailedMatter[];
}
type DetailedReport = UserDetailed[];
import DetailedTable from "./DetailedTable";
import ReactLoading from 'react-loading';
import { makeStyles } from "@material-ui/core";
import organization from "utils/organization";

const useStyles = makeStyles(() => ({
  loader: {
    fill: '#59abcb',
    margin: 'auto',
    marginTop: 50,
    marginBottom: 50,
    width: '10%',
  }
}));

/** Convert new UserMattersEntry[] to old DetailedReport shape for DetailedTable compatibility */
function toDetailedReport(data: UserMattersEntry[], users: PublicUserDto[]): DetailedReport {
  const userMap = new Map(users.map(u => [u._id, u]));
  return data.map(entry => {
    const user = userMap.get(entry.userId);
    return {
      displayName: entry.displayName,
      profilePicture: entry.profilePicture,
      title: entry.title,
      userId: entry.userId,
      currentWorkload: (user as any)?.currentWorkload ?? null,
      onLeave: (user as any)?.onLeave ?? null,
      matters: entry.matters.map(m => ({
        displayName: m.matterName,
        clientDisplayName: m.clientName,
        clientCountry: m.clientCountry,
        confidential: m.confidential,
        number: m.matterExternalId,
        nonBillable: m.nonBillable,
        totalHours: m.totalHours,
        resposibleTimekeeperName: m.leadName,
        comment: m.comment,
        manager: m.manager,
      })),
    };
  });
}

const DetailedPage = ({
  setLayout,
  filteredUsers,
  searchTerm = '',
}: {
  setLayout: (layout: string) => void;
  filteredUsers?: PublicUserDto[];
  searchTerm?: string;
}) => {
  const classes = useStyles();
  const { data: currentUser } = useGetCurrentUserQuery();
  const org = organization(currentUser?.company);

  const userIds = useMemo(() => filteredUsers?.map(u => u._id).filter(Boolean) as string[] || [], [filteredUsers]);
  const { data: rawData, isLoading } = useBulkRecentMattersQuery(
    { userIds, days: 14 },
    { skip: !userIds.length, refetchOnMountOrArgChange: 60 }
  );

  const detailedReport = useMemo(() => {
    if (!rawData || !filteredUsers) return undefined;
    return toDetailedReport(rawData, filteredUsers);
  }, [rawData, filteredUsers]);

  return (
    <>
      {detailedReport && <DetailedTable filteredUsers={filteredUsers} detailedReport={detailedReport} setLayout={setLayout} filterTerm={searchTerm} />}
      {isLoading && <ReactLoading type={'spinningBubbles'} className={classes.loader} />}
    </>
  );
};

export default DetailedPage;
